/**
 * Indian Currency (INR) Utility Functions
 *
 * Centralized currency formatting for the entire application.
 * All amounts are in Indian Rupees (₹) with en-IN locale.
 *
 * Indian numbering: 1,00,000 (one lakh) not 100,000
 * Financial year: April 1 to March 31
 */
/**
 * Format amount as Indian Rupees with ₹ symbol
 * Uses Indian numbering system (lakhs, crores)
 *
 * @param amount - Number to format
 * @param showDecimals - Show .00 decimals (default: false, use true for accounting/reports)
 * @returns Formatted string like "₹ 1,50,000" or "₹ 1,50,000.00"
 *
 * Examples:
 *   formatINR(150000)        → "₹ 1,50,000"
 *   formatINR(150000, true)  → "₹ 1,50,000.00"
 *   formatINR(null)          → "₹ 0"
 *   formatINR(0)             → "₹ 0"
 */
export var formatINR = function (amount, showDecimals) {
    if (showDecimals === void 0) { showDecimals = false; }
    if (amount === null || amount === undefined || isNaN(amount)) {
        return showDecimals ? '₹ 0.00' : '₹ 0';
    }
    var formatted = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0
    }).format(amount);
    return formatted.replace('₹', '₹ ');
};
/**
 * Format amount as Indian number WITHOUT ₹ symbol
 * Useful for data table cells where symbol is in the header
 *
 * @param amount - Number to format
 * @param showDecimals - Show .00 decimals (default: false)
 * @returns Formatted string like "1,50,000" or "1,50,000.00"
 *
 * Examples:
 *   formatAmount(150000)       → "1,50,000"
 *   formatAmount(150000, true) → "1,50,000.00"
 *   formatAmount(null)         → "0"
 */
export var formatAmount = function (amount, showDecimals) {
    if (showDecimals === void 0) { showDecimals = false; }
    if (amount === null || amount === undefined || isNaN(amount)) {
        return showDecimals ? '0.00' : '0';
    }
    return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0
    }).format(amount);
};
/**
 * Convert number to Indian words for receipts/vouchers
 *
 * @param amount - Number to convert
 * @returns Amount in words like "One Lakh Fifty Thousand Rupees Only"
 *
 * Examples:
 *   amountInWords(150000) → "One Lakh Fifty Thousand Rupees Only"
 *   amountInWords(1500.50) → "One Thousand Five Hundred Rupees and Fifty Paise Only"
 */
export var amountInWords = function (amount) {
    if (amount === null || amount === undefined || isNaN(amount) || amount === 0) {
        return 'Zero Rupees Only';
    }
    var ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    var tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    var convertBelowHundred = function (n) {
        if (n < 20)
            return ones[n];
        return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    };
    var convertBelowThousand = function (n) {
        if (n < 100)
            return convertBelowHundred(n);
        return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertBelowHundred(n % 100) : '');
    };
    // Indian numbering: Crore (10^7), Lakh (10^5), Thousand (10^3), Hundred (10^2)
    var rupees = Math.floor(Math.abs(amount));
    var paise = Math.round((Math.abs(amount) - rupees) * 100);
    var words = '';
    if (rupees >= 10000000) {
        words += convertBelowThousand(Math.floor(rupees / 10000000)) + ' Crore ';
    }
    if (rupees >= 100000) {
        words += convertBelowHundred(Math.floor((rupees % 10000000) / 100000)) + ' Lakh ';
    }
    if (rupees >= 1000) {
        words += convertBelowHundred(Math.floor((rupees % 100000) / 1000)) + ' Thousand ';
    }
    if (rupees >= 1) {
        words += convertBelowThousand(rupees % 1000);
    }
    words = words.trim();
    if (rupees === 0 && paise > 0) {
        return convertBelowHundred(paise) + ' Paise Only';
    }
    var result = words + ' Rupees';
    if (paise > 0) {
        result += ' and ' + convertBelowHundred(paise) + ' Paise';
    }
    result += ' Only';
    return result;
};
/**
 * Parse Indian formatted currency string back to number
 *
 * @param formatted - String like "₹ 1,50,000" or "1,50,000.00"
 * @returns Numeric value
 *
 * Examples:
 *   parseINR("₹ 1,50,000")    → 150000
 *   parseINR("1,50,000.50")   → 150000.50
 *   parseINR("")              → 0
 */
export var parseINR = function (formatted) {
    if (!formatted)
        return 0;
    var cleaned = formatted.replace(/[₹,\s]/g, '');
    var parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
};
